import { Button } from '@base-ui/react/button';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import type { components } from '@/api/qraft/schema';
import { announceSaved } from '@/saveBanner';
import { ConfirmDialog } from '~/components/ui/ConfirmDialog';
import {
  LessonHeader,
  TabHeading,
  TopicHeader,
} from '~/components/ui/ContentNav';
import {
  type ContentSearchParams,
  parseContentSearch,
  SearchFilters,
  searchQuery,
} from '~/components/ui/ContentSearch';
import { ErrorNotice } from '~/components/ui/Form';
import { PageTitle } from '~/components/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '~/components/ui/Pagination';
import { finalQuizTitle } from '~/components/ui/QuizBadge';
import { LockedQuizNotice } from '~/components/ui/QuizLock';
import { Table } from '~/components/ui/Table';
import { LANGUAGE_LABELS } from '~/constants/languages';
import buttonStyles from '~/styles/button.module.css';
import { QUESTION_TYPE_LABELS } from './QuestionEditor';

type Question = components['schemas']['QuestionAdminSummary'];
type QuestionSearchItem = components['schemas']['QuestionSearchItem'];
type QuestionType = components['schemas']['QuestionTypes'];
type BulkAction = 'delete' | 'publish';

const PAGE_SIZE = 50;

export const Route = createFileRoute('/_admin/questions/')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { lesson?: number; topic?: number } & ContentSearchParams => ({
    // With a lesson, one quiz's questions; without, a search across every quiz.
    lesson: s.lesson != null ? Number(s.lesson) : undefined,
    // No topic means the lesson's final quiz.
    topic: s.topic != null ? Number(s.topic) : undefined,
    ...parseContentSearch(s, QUESTION_TYPES),
  }),
  component: QuestionsPage,
});

const QUESTION_TYPES = Object.keys(QUESTION_TYPE_LABELS) as QuestionType[];

// Worded like a single delete's prompt (`deletePrompt`), for a selection.
function bulkDeletePrompt(inDraft: Question[]): string {
  const copies = inDraft.filter((q) => q.lineage_id !== q.id).length;
  if (copies === 0) return "This can't be undone.";
  if (copies === inDraft.length)
    return 'You can restore them from Review changes until this draft is published.';
  return 'Ones from the live version can be restored from Review changes; new ones can’t.';
}

function QuestionsPage() {
  const { lesson, topic } = Route.useSearch();
  return lesson !== undefined ? (
    <QuizQuestions lesson={lesson} topic={topic} />
  ) : (
    <QuestionSearch />
  );
}

const columnHelper = createColumnHelper<Question>();

const canDelete = (q: Question) => !q.locked;
const canPublish = (q: Question, quizLocked: boolean) =>
  q.status === 'draft' && !quizLocked;

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

type TableMeta = {
  quizLocked: boolean;
  selected: Set<number>;
  selectable: Question[];
  toggle: (id: number) => void;
  toggleAll: () => void;
};

const metaOf = (table: { options: { meta?: unknown } }) =>
  table.options.meta as TableMeta;

// Defined once: a cell renderer rebuilt per render would remount its checkbox
// on every tick, losing keyboard focus.
const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      const { selected, selectable, toggleAll } = metaOf(table);
      const all = selectable.length > 0 && selected.size === selectable.length;
      return (
        <SelectCell>
          <input
            type="checkbox"
            aria-label="Select every question on this page"
            checked={all}
            disabled={selectable.length === 0}
            ref={(el) => {
              if (el) el.indeterminate = selected.size > 0 && !all;
            }}
            onChange={toggleAll}
          />
        </SelectCell>
      );
    },
    cell: ({ row, table }) => {
      const { quizLocked, selected, toggle } = metaOf(table);
      const q = row.original;
      const selectable = canDelete(q) || canPublish(q, quizLocked);
      return (
        // Ticking a box shouldn't open the question.
        <SelectCell onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Select question ${q.id}`}
            checked={selected.has(q.id)}
            disabled={!selectable}
            title={
              selectable
                ? undefined
                : "Students may have access to this question, so it can't be deleted"
            }
            onChange={() => toggle(q.id)}
          />
          {q.locked && (
            <LockIcon
              icon={faLock}
              title="Students may have access to this question, so only its wording can change"
            />
          )}
        </SelectCell>
      );
    },
  }),
  columnHelper.accessor('order', { header: 'Order' }),
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: (info) => QUESTION_TYPE_LABELS[info.getValue()],
  }),
  columnHelper.accessor('title', {
    header: 'Question',
    // Drafts may be saved before their text is written.
    cell: (info) => info.getValue() || '(no question text)',
  }),
  columnHelper.accessor('score', { header: 'Points' }),
  columnHelper.accessor('status', {
    header: 'Status',
    // A draft in a locked quiz stays a draft; say so where it's listed.
    cell: (info) =>
      metaOf(info.table).quizLocked && info.getValue() === 'draft'
        ? "draft (can't be published)"
        : info.getValue(),
  }),
];

function QuizQuestions({ lesson, topic }: { lesson: number; topic?: number }) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirming, setConfirming] = useState<BulkAction | null>(null);

  // Admin read: every status, so drafts can be finished and published.
  const { data, isLoading, error } = api.adminListQuestions.useQuery({
    path: { lesson_id: lesson },
    query: {
      ...(topic ? { topic_id: topic } : {}),
      limit: PAGE_SIZE,
      offset: PAGE_SIZE * page,
    },
  });

  // A topic quiz follows its topic; the final quiz follows the lesson.
  const lessonQuery = api.adminGetLesson.useQuery({
    path: { lesson_id: lesson },
  });
  const topicQuery = api.adminGetTopic.useQuery(
    { path: { topic_id: topic ?? 0 } },
    { enabled: topic !== undefined },
  );
  const parentData = topic !== undefined ? topicQuery.data : lessonQuery.data;
  const quizLocked = parentData?.quiz_locked ?? false;
  // In a draft, copies of live questions come back from Review changes; ones
  // written in the draft don't.
  const inDraft = parentData?.version_state === 'draft';

  const rows = data?.data ?? [];
  const selectable = rows.filter(
    (q) => canDelete(q) || canPublish(q, quizLocked),
  );
  const allSelected =
    selectable.length > 0 && selected.size === selectable.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectable.map((q) => q.id)));
  const chosen = rows.filter((q) => selected.has(q.id));
  const toDelete = chosen.filter(canDelete);
  const toPublish = chosen.filter((q) => canPublish(q, quizLocked));

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      quizLocked,
      selected,
      selectable,
      toggle: (id: number) =>
        setSelected((prev) => {
          const next = new Set(prev);
          if (!next.delete(id)) next.add(id);
          return next;
        }),
      toggleAll,
    } satisfies TableMeta,
  });

  const changePage = (next: number) => {
    setSelected(new Set());
    setPage(next);
  };

  // Deleting every question on the last page leaves it empty.
  useEffect(() => {
    if (data && data.data.length === 0 && page > 0) setPage(page - 1);
  }, [data, page]);

  const refreshQuiz = () => {
    queryClient.invalidateQueries({
      queryKey: api.adminListQuestions.getQueryKey({
        path: { lesson_id: lesson },
      }),
    });
    // Their quiz counts feed the publish warnings.
    queryClient.invalidateQueries({
      queryKey: api.adminGetLesson.getQueryKey({
        path: { lesson_id: lesson },
      }),
    });
    if (topic !== undefined)
      queryClient.invalidateQueries({
        queryKey: api.adminGetTopic.getQueryKey({ path: { topic_id: topic } }),
      });
  };

  const afterBulk = (message: string) => {
    setSelected(new Set());
    setConfirming(null);
    announceSaved(message);
    refreshQuiz();
  };
  // A refusal can mean the rows changed under us (deleted, or now answered).
  const onBulkError = () => {
    setConfirming(null);
    refreshQuiz();
  };

  const deleteMutation = api.adminBulkDeleteQuestions.useMutation(undefined, {
    // It announces how many itself.
    meta: { toast: false },
    onSuccess: (_, variables) =>
      afterBulk(
        `Deleted ${plural(variables.body.question_ids.length, 'question', 'questions')}.`,
      ),
    onError: onBulkError,
  });
  const publishMutation = api.adminBulkPublishQuestions.useMutation(undefined, {
    meta: { toast: false },
    onSuccess: (_, variables) =>
      afterBulk(
        `Published ${plural(variables.body.question_ids.length, 'question', 'questions')}.`,
      ),
    onError: onBulkError,
  });
  const busy = deleteMutation.isPending || publishMutation.isPending;
  const bulkError = deleteMutation.error ?? publishMutation.error;
  // `busy` lands a render late, so a fast double-click could send twice.
  const running = useRef(false);

  const runBulk = () => {
    if (running.current || confirming === null) return;
    running.current = true;
    const body = {
      topic_id: topic ?? null,
      question_ids: (confirming === 'delete' ? toDelete : toPublish).map(
        (q) => q.id,
      ),
    };
    deleteMutation.reset();
    publishMutation.reset();
    const mutation = confirming === 'delete' ? deleteMutation : publishMutation;
    mutation.mutate(
      { path: { lesson_id: lesson }, body },
      {
        onSettled: () => {
          running.current = false;
        },
      },
    );
  };

  if (isLoading) return <Message>Loading questions...</Message>;
  if (error) return <Message $error>Error loading questions</Message>;

  const total = data?.count ?? 0;
  const parent = topic !== undefined ? 'topic' : 'lesson';
  const pagination = (
    <Pagination
      page={page}
      pageSize={PAGE_SIZE}
      shown={rows.length}
      total={total}
      onPage={changePage}
    />
  );
  const confirmList = confirming === 'delete' ? toDelete : toPublish;
  const notDeletable = chosen.length - toDelete.length;
  const notDrafts = chosen.length - toPublish.length;
  const deleteLabel = plural(toDelete.length, 'question', 'questions');
  const publishLabel = plural(toPublish.length, 'draft', 'drafts');

  return (
    <div>
      {topic !== undefined
        ? topicQuery.data && <TopicHeader topic={topicQuery.data} />
        : lessonQuery.data && <LessonHeader lesson={lessonQuery.data} />}
      <TabHeading
        action={
          quizLocked ? (
            <Button className={buttonStyles.Button} disabled>
              Add question
            </Button>
          ) : (
            <Link
              to="/questions/new"
              search={{ lesson, topic }}
              className={`${buttonStyles.Button} ${buttonStyles.Primary}`}
            >
              Add question
            </Link>
          )
        }
      >
        {topic !== undefined
          ? 'Topic quiz'
          : finalQuizTitle(lessonQuery.data?.title)}
      </TabHeading>
      {quizLocked && <LockedQuizNotice parent={parent} />}

      <Status aria-live="polite">
        {bulkError && (
          <ErrorNotice role="alert">
            {apiErrorDetail(bulkError) ??
              'Something went wrong, and nothing was changed. Please try again.'}
          </ErrorNotice>
        )}
      </Status>

      {selected.size > 0 && (
        <BulkBar role="region" aria-label="Selected questions">
          <BulkSelectAll>
            <input
              type="checkbox"
              aria-label="Select every question on this page"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = !allSelected;
              }}
              onChange={toggleAll}
            />
          </BulkSelectAll>
          <strong>{selected.size} selected</strong>
          <BulkActions>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Primary}`}
              disabled={toPublish.length === 0 || busy}
              onClick={() => setConfirming('publish')}
            >
              {toPublish.length === 0 ? 'Publish' : `Publish ${publishLabel}`}
            </Button>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Danger}`}
              disabled={toDelete.length === 0 || busy}
              onClick={() => setConfirming('delete')}
            >
              {toDelete.length === 0 ? 'Delete' : `Delete ${deleteLabel}`}
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
          </BulkActions>
          {(notDeletable > 0 || notDrafts > 0) && (
            <BulkHint>
              {notDeletable > 0 &&
                `${plural(notDeletable, 'selected question is', 'selected questions are')} locked, so won't be deleted. `}
              {notDrafts > 0 &&
                (quizLocked
                  ? "Drafts in this quiz can't be published."
                  : `${plural(notDrafts, 'selected question is', 'selected questions are')} already published.`)}
            </BulkHint>
          )}
        </BulkBar>
      )}

      {total > PAGE_SIZE && pagination}

      {total === 0 ? (
        <Message>{quizLocked ? 'No questions.' : 'No questions yet.'}</Message>
      ) : (
        <Table
          table={table}
          to="/questions/$questionId"
          getParams={(row) => ({ questionId: String(row.id) })}
          isSelected={(row) => selected.has(row.id)}
        />
      )}
      {total > PAGE_SIZE && pagination}

      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming === 'delete'
            ? `Delete ${deleteLabel}?`
            : `Publish ${publishLabel}?`
        }
        confirmLabel={
          busy
            ? 'Working...'
            : confirming === 'delete'
              ? `Delete ${deleteLabel}`
              : `Publish ${publishLabel}`
        }
        cancelLabel="Cancel"
        tone={confirming === 'delete' ? 'danger' : 'primary'}
        pending={busy}
        onConfirm={runBulk}
        onCancel={() => setConfirming(null)}
      >
        <ConfirmList>
          {confirmList.slice(0, 8).map((q) => (
            <li key={q.id}>
              #{q.id} {q.title || '(no question text)'}
            </li>
          ))}
          {confirmList.length > 8 && (
            <li>and {plural(confirmList.length - 8, 'other', 'others')}</li>
          )}
        </ConfirmList>
        {confirming === 'delete' ? (
          <p>{bulkDeletePrompt(inDraft ? toDelete : [])}</p>
        ) : (
          <p>
            Every draft has to be ready to publish, or none are. Once students
            can reach this {parent}, published questions can only have their
            wording changed.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}

const searchColumnHelper = createColumnHelper<QuestionSearchItem>();

const searchColumns = [
  searchColumnHelper.accessor('id', { header: 'ID' }),
  searchColumnHelper.accessor('title', {
    header: 'Question',
    cell: (info) => info.getValue() || '(no question text)',
  }),
  searchColumnHelper.accessor('type', {
    header: 'Type',
    cell: (info) => QUESTION_TYPE_LABELS[info.getValue()],
  }),
  searchColumnHelper.accessor('topic_title', {
    header: 'Quiz',
    cell: (info) =>
      info.getValue() ?? finalQuizTitle(info.row.original.lesson_title),
  }),
  searchColumnHelper.accessor('lesson_title', { header: 'Lesson' }),
  searchColumnHelper.accessor('language', {
    header: 'Language',
    cell: (info) => LANGUAGE_LABELS[info.getValue()],
  }),
  searchColumnHelper.accessor('status', { header: 'Status' }),
];

function QuestionSearch() {
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const page = (params.page ?? 1) - 1;

  const { data, isLoading, error } = api.adminSearchQuestions.useQuery({
    query: {
      ...searchQuery(params, LIST_PAGE_SIZE),
      ...(params.type ? { type: params.type } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns: searchColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const update = (patch: ContentSearchParams, replace = true) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={(next) =>
        update({ page: next > 0 ? next + 1 : undefined }, false)
      }
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      <PageTitle>Questions</PageTitle>
      <Intro>
        Every question in every quiz, drafts included. Searches the question
        text too.
      </Intro>
      <SearchFilters
        params={params}
        onChange={update}
        placeholder="Search questions by text or ID"
        typeLabels={QUESTION_TYPE_LABELS}
      />
      {isLoading ? (
        <Message>Loading questions...</Message>
      ) : error ? (
        <Message $error>Error loading questions</Message>
      ) : data?.count === 0 ? (
        <Message>No questions match.</Message>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/questions/$questionId"
            getParams={(row) => ({ questionId: String(row.id) })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

const Intro = styled.p`
  color: var(--grey-700);
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
`;

const Status = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

// Stays in view while scrolling a long page of ticked rows.
const BulkBar = styled.div`
  align-items: center;
  background: var(--component-color);
  border: 1px solid var(--blue);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  position: sticky;
  top: 0.5rem;
  z-index: 5;
`;

const BulkSelectAll = styled.label`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  margin: -0.5rem 0 -0.5rem -0.25rem;
  padding: 0.5rem;

  input {
    cursor: pointer;
    height: 1rem;
    width: 1rem;
  }
`;

const BulkActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

const BulkHint = styled.p`
  color: var(--grey-700);
  flex-basis: 100%;
  font-size: 0.8125rem;
  margin: 0;
`;

const SelectCell = styled.label`
  align-items: center;
  cursor: default;
  display: inline-flex;
  gap: 0.4rem;
  margin: -0.75rem;
  padding: 0.75rem;

  input {
    cursor: pointer;
    height: 1rem;
    width: 1rem;
  }

  input:disabled {
    cursor: not-allowed;
  }
`;

const LockIcon = styled(FontAwesomeIcon)`
  color: var(--grey-550);
  font-size: 0.75rem;
`;

const ConfirmList = styled.ul`
  max-height: 12rem;
  overflow-y: auto;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
